import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  Subject,
  combineLatest,
} from 'rxjs';
import {
  AppCountry,
  CmsCountryObj,
  EstimateLabels,
  Labels,
  SdfCardLabels,
  StatesObj,
  SectionItem,
} from '../app.models';
import {
  config as countryConfig,
  getCountryConfig,
} from '../config/country-config';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Store } from '@ngrx/store';
import {
  defaultCountry,
  isCashOutAllowed,
} from '../store/service-options/service-options.selectors';
import { ServiceOptionsState } from '../store/service-options';
import {
  isStatesAllowed,
  statesAllowedIso3Countries,
} from '../config/app-config';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { StringUtils } from '../shared/utils/string-utils';
import { CountryObj } from '../store/types';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { CountryData } from './custom-country-input-selector/custom-country-input-selector.component';
import {
  getAmoutSummary,
  getFxRate,
} from '../store/fee-quote/fee-quote.selectors';
import { isEmpty } from 'ramda';
import { FxRateResults, FxRequest } from '../store/fee-quote/types';
import { GetFxRateAction } from '../store/fee-quote';
import { queryAmount } from '../store/query-params';
import { ID_TYPES } from "../store/fee-quote/types"; 
import { LocationSelectService } from '../services/location-select.service';
import { MgLocation } from '../models/location.model';
import { PaymentOptionType } from '../feature/payment-method/payment-method.models';

export interface EstimateValue {
  amount: number;
  country: string;
  state?: string;
  currency: string | undefined;
}

export interface StatesConfig {
  code: string;
  name: string;
}

export interface SummaryItem {
  name: string;
  value: number;
  sendCurrency: string;
  isBold: boolean;
  displayMinus: boolean;
}

@Component({
  selector: 'app-card-estimate',
  templateUrl: './card-estimate.component.html',
  styleUrls: ['./card-estimate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardEstimateComponent implements OnInit, OnDestroy {
  form: FormGroup;
  countryControl = new FormControl('', [Validators.required]);
  stateControl = new FormControl('', [Validators.required]);
  states: StatesConfig[] | any;
  defaultStates: StatesConfig[];
  allowed: string = 'true';
  estimateLabels: EstimateLabels;
  csmCountries: any[];
  cardEstimates: EstimateValue;
  summarySections$: Observable<any> | undefined;
  summarySectionsTitle = '';
  totalAmountvalue: number = 0;
  stateCode: string;
  termsAndCondPartnerFee:boolean = false;
  @Output() public formValueChanges: EventEmitter<EstimateValue> =
    new EventEmitter<EstimateValue>();
  @Output() public next: EventEmitter<any> = new EventEmitter<any>();

  countries: AppCountry[] = countryConfig;
  countriesFromStore: any;
  defaultCountryObj: CmsCountryObj;
  countryCodes: string[];

  private _watches: Subscription[] = [];
  filteredCountries: Observable<CountryObj[]>;
  filteredOptions: Observable<StatesObj[]>;
  countryArrowIcon: string = 'keyboard_arrow_down';
  stateArrowIcon: string = 'keyboard_arrow_down';
  arrowIcon = 'keyboard_arrow_down';
  disableCashInCountry: boolean = true;
  amountDetails: any;
  public isLocationSelected: boolean;
  public isAmountGreaterThanZero: boolean | null = null;
  
  public selectedPaymentMethod: PaymentOptionType | null = null;
  public isPaymentOptionsEnabled: boolean = false;
  public isCashEnabled: boolean = false;
  public isDebitEnabled: boolean = false;
  public isBankAccountEnabled: boolean = false;

  isSdfCardFlow = false;
  private _enableSelectLocationBtn$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public enableSelectLocationBtn$: Observable<boolean> =
    this._enableSelectLocationBtn$.asObservable();

  private subscriptions: Subscription = new Subscription();

  public _google: any;
  @Input() public set google(map: any) {
    this._google = map;
  }
  public get google() {
    return this._google;
  }

  private _selectedLocation: any;
  @Input() public set selectedLocation(location: any) {
    this._selectedLocation = location;
    this.isLocationSelected = !!location?.locationDetails;
  }
  public get selectedLocation() {
    return this._selectedLocation;
  }
  @Input() locale!: string;
  @Input() indicativeRateCountry!: boolean;
  @Input() totalAmountToPay = 0;
  @Input() totalAmountCurrency = 'USD';
  @Input() fees = 0;
  @Input() feesCurrencyCode = 'USD';
  @Input() taxes = 0;
  @Input() taxesCurrencyCode = 'USD';
  @Input() isWalletFeeEnabled: any;
  
  private _amount?: string;
  isCashoutAllowed$: Observable<boolean> | undefined;
  fxRateObj: FxRateResults;
  sdfCardLabels: SdfCardLabels;
  _fxRateErrorBehaviorSubject$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  fxRateError$: Observable<boolean> =
    this._fxRateErrorBehaviorSubject$.asObservable();

  private destroy$: Subject<void> = new Subject<void>();

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this.sdfCardLabels = labels.stellar.sdfcard;
    this._labels = labels;
    this.estimateLabels = labels?.stellar?.estimate;
    this.csmCountries = this.countries.map(
      (country) => labels?.stellar?.country[country?.code]
    );
  }
  public get labels() {
    return this._labels;
  }

  @Input() public set amount(amount: number | string) {
    if (typeof amount === 'number') {
      this._amount = amount.toFixed(2);
    } else {
      this._amount =
        (amount &&
          !Number.isNaN(parseFloat(amount)) &&
          parseFloat(amount).toFixed(2)) ||
        undefined;
    }
  }
  public get amount() {
    return this._amount!;
  }

  public _disableAmount?: boolean;
  @Input() public set disableAmount(disable: boolean | string) {
    this._disableAmount = coerceBooleanProperty(disable);
  }
  public get disableAmount() {
    return this._disableAmount!;
  }

  public get isFormvalid(): boolean {
    return this.flow === 'deposit'
      ? !isEmpty(this.selectedLocation) &&
          this.form.valid &&
          this.totalAmountvalue > 0
      : this.form.valid && this.totalAmountvalue > 0;
  }

  // public selectLocationButtonEnabled(): boolean | null {
  //   // return this.totalAmountvalue > 0 ? true : false;
  // }

  public get enableSelectLocationBtn(): boolean {
    const isCountrySelected =
      this.form.get('country')?.value !== undefined &&
      this.form.get('country')?.value?.trim() !== '';
    // const isStateselected = !this.form.get('state') || ( this.form.get('state')?.value.trim() !== '')
    const isAmountValid =
      this.form.get('amount')?.value !== undefined &&
      Number(this.form.get('amount')?.value) > 0;
    const isFormValid = this.form.valid;
    const depositFlow = this.flow === 'deposit';
    return isCountrySelected && isAmountValid && isFormValid && depositFlow;
  }

  @Input() flow!: string;
  @Input() token!: string;
  @Input() cmsMappingCountries: CmsCountryObj;

  @Input() public set options(options: ServiceOptionsState) {
    this._serviceOptions = options;
    this.disableCashInCountry =
      options?.uiConfigs[0]?.disableCashInCountry !== undefined
        ? options?.uiConfigs[0]?.disableCashInCountry
        : true;
    this.isPaymentOptionsEnabled = options.features?.isPaymentOptionsEnabled ?? false;
    this.isCashEnabled = options.features?.isCashEnabled ?? false;
    this.isDebitEnabled = options.features?.isDebitEnabled ?? false;
    this.isBankAccountEnabled = options.features?.isBankAccountEnabled ?? false;
    this._receiveCurrencies =
      options?.serviceOptions[0]?.sendCurrencyCodes ||
      options?.serviceOptions[0]?.payoutCurrencyCodes;
    this.countryCodes =
      options?.cashOutCountries.length > 1
        ? options?.cashOutCountries
        : options?.cashInCountries;
    const localCurrency = options?.serviceOptions[0]?.localCurrencyCode;
    // if receives currencies contains local currency then consider local currency otherwise take first element of receive cureencies
    this._receiveCurrency = !!this._receiveCurrencies
      ? this._receiveCurrencies.some((currency) => currency === localCurrency)
        ? localCurrency
        : this._receiveCurrencies[0]
      : 'USD';
    this.countriesFromStore = options?.countriesDetails;
  }

  private _country?: string;
  @Input() public set countryDetails(countryDetails: CmsCountryObj) {
    if (countryDetails) {
      this.defaultCountryObj = {
        code: countryDetails?.code,
        name: countryDetails?.name,
        iso2code: countryDetails?.iso2code,
        states: countryDetails.states,
      };
      this._country = countryDetails?.code;
      this._countryName = countryDetails?.name;
      const states =
        this._country && isStatesAllowed(this._country)
          ? countryDetails && countryDetails.states
          : [];
      this._updateStateControl(states);
      this.defaultStates =
        this._country && this._country === 'USA'
          ? this.filterStates(states)
          : states;
      this.states = this.defaultStates;

      if (this._country !== this.form?.get('country')?.value) {
        if (this.disableCashInCountry) {
          this.form?.get('country')?.enable();
        }

        this.form?.get('country')?.setValue(this._country);

        if (this.disableCashInCountry && this.flow !== 'withdraw') {
          this.form?.get('country')?.disable();
        }
      }
    }
  }

  private _endpoint: string;
  @Input() public set endpoint(endpointValue: string) {
    this._endpoint = endpointValue;
  }
  public get endpoint() {
    return this._endpoint;
  }

  _serviceOptions?: ServiceOptionsState;
  _receiveCurrencies: string[] = [];
  public get receiveCurrencies() {
    return this._receiveCurrencies;
  }

  _receiveCurrency?: string;
  public get receiveCurrency() {
    return this._receiveCurrency;
  }

  _countryName?: string;
  public get countryName() {
    return this._countryName;
  }

  public get iso2Code() {
    return this._country && getCountryConfig(this._country)?.iso2Code;
  }

  public get value() {
    const currentVal = this.form?.getRawValue();
    return (
      currentVal && {
        ...currentVal,
        amount: currentVal.amount ? parseFloat(currentVal.amount) : undefined,
        currency: this.receiveCurrency,
      }
    );
  }

  @ViewChildren(MatAutocompleteTrigger)
  triggers!: QueryList<MatAutocompleteTrigger>;

  constructor(
    private _fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store<any>,
    private locSelectService: LocationSelectService
  ) {
    this.form = this._fb.group({
      state: this.stateControl,
      country: this.countryControl,
      amount: new FormControl('', [Validators.required, Validators.min(1)]),
    });
  }

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((val) => {
        const amountIsGreaterZero =
          this.cardEstimates['amount'] >= 1 && +val['amount'] >= 1;
        if (
          this.cardEstimates &&
          !val['state'] &&
          !this.cardEstimates['state'] &&
          amountIsGreaterZero
        ) {
          setTimeout(() => {
            this.emitFormValues();
          }, 100);
        }
      });
    if (this._amount !== this.form?.get('amount')?.value) {
      this.form?.get('amount')?.setValue(this._amount);

      if (this._disableAmount) {
        this.form?.get('amount')?.disable();
      }
    }

    this.form.valueChanges.subscribe((val) => {
      this._enableSelectLocationBtn$.next(this.enableSelectLocationBtn)
      this.isAmountGreaterThanZero = val.amount > 0;
    });
    this.store.select(isCashOutAllowed).subscribe((allowed) => {
      this.allowed = allowed;
    });

    this.store.select(getFxRate).subscribe((fx) => {
      // console.log('WHat is fxRate from store', fx)
      if (!isEmpty(fx) && Number(fx.fxRate) > 0) {
        this.fxRateObj = fx;
        this._fxRateErrorBehaviorSubject$.next(false);
      } else {
        this._fxRateErrorBehaviorSubject$.next(true);
      }
    });

    this.termsAndCondPartnerFee = true;
    
    this.summarySections$ = this.store.select(getAmoutSummary);
    
    this.summarySections$.subscribe((summaryArr: SectionItem[]) => {
      this.totalAmountvalue = summaryArr.reduce((acum, j) => (j.id_type === ID_TYPES.TOTAL_PAY_AMOUNT) ? Number(j.value) + acum : 0, 0);
    });

    if (this.flow === 'deposit' || this.states.length === 0) {
      this.form.removeControl('state');
    }

    this.subscriptions.add(
      combineLatest([
        this.store.select(defaultCountry),
        this.store.select(queryAmount),
      ]).subscribe(([country, amount]) => {
        if (country && amount) {
          this.cardEstimates = {
            amount: 0,
            country: '',
            state: '',
            currency: '',
          };
          this.cardEstimates['country'] = country;
          this.cardEstimates['amount'] = amount && amount > 0 ? amount : 0;
          this.emitFormValues();
        }
      })
    );

    this.stateControl.valueChanges
      .pipe(debounceTime(100))
      .subscribe((value) => {
        this.states.map((state: StatesObj) => {
          if (
            StringUtils.removeDiacriticsAndConvertToLowercase(state.name) ===
            StringUtils.removeDiacriticsAndConvertToLowercase(value!)
          ) {
            this.stateCode = state.code;
            this.stateControl.patchValue(this.stateCode);
          }
        });
        if (this.stateCode) {
          this.emitFormValues();
        }
      });

    if (this._disableAmount) {
      this.form.get('amount')?.disable();
    }

    if (this.disableCashInCountry && this.flow !== 'withdraw') {
      this.form.get('country')?.disable();
    }

    this._watches.push(
      this.form.valueChanges
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        ) // To avoid infinite loop
        .subscribe((change) => this._handleFormChange(change))
    );
  }

  setStatePair(countryData: CountryData) {
    if (
      countryData.countryCode &&
      countryData.defaultCountry === countryData.countryCode &&
      countryData.isSupportedCountry
    ) {
      if (!!this.defaultStates) {
        this.states = this.defaultStates;
      }
    }

    if (!countryData.isSupportedCountry) {
      this.states = [];
      this.cdr.detectChanges();
    }
  }

  public nextPage() {
    this.next.emit(1);
  }

  public statePanelToggle(isOpen: boolean): void {
    if (isOpen) {
      this.stateArrowIcon = 'keyboard_arrow_up';
    } else {
      this.stateArrowIcon = 'keyboard_arrow_down';
    }
  }

  public getSelectedOptionLabel(options: any, key: string, fieldValue: any) {
    if (!fieldValue) {
      return '';
    }
    const index = options?.findIndex(
      (option: any) => option[key] === fieldValue
    );
    return options[index]?.name;
  }

  public formatAmount(): void {
    const control = this.form?.get('amount')!;
    this.amount = control.value;

    if (this._amount !== control.value) {
      control.patchValue(this._amount);
    }
    this.emitFormValues();
  }

  public changeCurrency(rc: any): void {
    this._receiveCurrency = rc;
    this.cardEstimates['currency'] = this._receiveCurrency;
    if (this._receiveCurrency && !this.cardEstimates['amount']) {
      this.triggerFxRate();
    } else {
      this.emitFormValues();
    }
  }

  triggerFxRate() {
    let payLoad: FxRequest = {
      destinationCountryCode: this.cardEstimates.country,
      originatingCountryCode: 'USA',
      operation: this.flow,
      receiveCurrencyCode: this._receiveCurrency,
      sendCurrencyCode: 'USD',
      targetAudience: 'CONSUMER_FACING',
      userLanguage: 'en-US',
    };
    // console.log('What is the payload data', payLoad)
    this.store.dispatch(
      GetFxRateAction({ fxRequest: payLoad, endpoint: this.endpoint })
    );
  }

  backToWalletDone() {
    setTimeout(() => {
      window.close();
    }, 400);
  }

  private _handleFormChange(estValue: EstimateValue): void {
    if (this._amount) {
      estValue.amount = Number(this._amount);
    }
    if (estValue.state) {
      this.stateCode = this.getStateCode(estValue.state)!;
      if (this.stateCode) {
        estValue.state = this.stateCode;
      } else {
        this.form.get('state')?.setErrors({ invalidOption: true });
      }
    }
    if (estValue.country) {
      const countryCode = this.getCountryCode(estValue.country);
      // 1. If CC not exist,then its not a valid country option
      // 2. If CC exisits anf not supported, then it's not a supported country Ex: Nepal
      if (countryCode) {
        estValue.country = countryCode;
        const isSupportedCountry = this.countryCodes.find(
          (code) => code === countryCode
        );
        if (!isSupportedCountry) {
          this.form.get('country')?.setErrors({ notSupported: true });
        } else {
          this.form.get('country')?.setErrors(null);
        }
      } else {
        this.form.get('country')?.setErrors({ invalidOption: true });
      }
    }

    if (estValue.state) {
      estValue.state = this.getStateCode(estValue.state);
    }
    if (estValue.country) {
      estValue.country = this.getCountryCode(estValue.country);
    }
    estValue.currency = this._receiveCurrency;
    this.cardEstimates = estValue;
    // this.formValueChanges.emit(estValue)
  }

  emitFormValues() {
    if (
      this.cardEstimates &&
      this.cardEstimates.country &&
      this.cardEstimates.amount
    ) {
      if (this.flow === 'withdraw') {
        // ToDo remove once state dropdown is implemented
        if (
          (statesAllowedIso3Countries.indexOf(this.cardEstimates.country) >
            -1 &&
            this.states.length > 1 &&
            this.cardEstimates.state) ||
          statesAllowedIso3Countries.indexOf(this.cardEstimates.country) === -1
        ) {
          setTimeout(() => {
            this.formValueChanges.emit(this.cardEstimates);
          }, 100);
        }
      }

      if (this.flow === 'deposit') {
        setTimeout(() => {
          this.formValueChanges.emit(this.cardEstimates);
        }, 10);
      }
    }
  }

  getStateCode(stateName: string): string | undefined {
    return !!this.states && stateName
      ? this.states.find(
          (state: any) => state.name === stateName || state.code === stateName
        )?.code
      : '';
  }

  getCountryCode(countryValue: string): any {
    const countryCode =
      this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) ==
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    if (countryCode) {
      return countryCode;
    } else {
      return this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country.code
              ) ===
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    }
  }

  filterStates(states: StatesConfig[]) {
    const filteredStates =
      !!states &&
      this._serviceOptions?.uiConfigs[0]?.notSupportedUSStates &&
      states.filter(
        (state) =>
          !this._serviceOptions?.uiConfigs[0]?.notSupportedUSStates.includes(
            state.code
          )
      );
    return filteredStates ? filteredStates : states;
  }

  formatSubmitValue(value: EstimateValue) {
    if (value.state) {
      value.state = this.getStateCode(value.state);
    }
    if (value.country) {
      value.country = this.getCountryCode(value.country);
    }
    return value;
  }

  private _updateStateControl(states: StatesConfig[]): void {
    if (
      this.flow === 'withdraw' &&
      states.length > 0 &&
      !this.form.get('state')
    ) {
      // States exist for country
      this.form.addControl('state', this.stateControl);
    } else if (this.flow === 'deposit' || states.length === 0) {
      // States DON'T exist for country
      this.form.removeControl('state');
    }
  }

  public getFxRateEstimateFootNote(): string {
    const defualtText = "At the time of payment, the exchange currency rate may differ, affecting your total pay amount.";
    const disclaimerText = this.sdfCardLabels.landingPage.disclaimer.three || defualtText
    if(this.termsAndCondPartnerFee) {
      return `3 ${disclaimerText}`
    } else {
      return `2 ${disclaimerText}`
    }
   }

  public openLocationSelectorDialog(): void {
    this.locSelectService.openDialog(this.labels, this.google, this.flow);
  }

  public setPaymentMethod(paymentMethod: PaymentOptionType): void {
    this.selectedPaymentMethod = paymentMethod;
  }

  public ngOnDestroy(): void {
    this._watches.forEach((subscription) => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  private closePanel() {
    this.triggers.forEach((trigger: MatAutocompleteTrigger) => {
      if (trigger.panelOpen) {
        setTimeout(() => trigger.closePanel());
      }
    });
  }
}
